import SectionTitle from "@Client/@Modules/application-standalone/components/helpers/section-title.vue";
import PageSectionGroupLayout from "@Client/@Modules/application-standalone/components/layout/page-section-group-layout.vue";
import { computed, defineComponent, toRefs, watch } from "@vue/composition-api";
import TextField from "@Shared/components/form-elements/fields/text-field.vue";
import SelectField from "@Shared/components/form-elements/fields/select-field.vue";
import CurrencyField from "@Shared/components/form-elements/fields/currency-field.vue";
import { useApplicantHelpers } from "@Client/@Composables/application-standalone/pages/applicants/useApplicantHelpers";
import { APPLICATION_FIELD_OPTIONS } from "@Shared/@Utils/application-defaults-utils/global-field-options";
import { useI18n } from "vue-i18n-composable";
import { ASSET_TYPE } from "@Shared/@Types/application/application-enums";
import { assetValidator } from "@Client/@Composables/application-standalone/validations/assetValidator";
import { useAssetHelpers } from "@Client/@Composables/application-standalone/pages/assets/useAssetHelpers";
import { assetsPageBindingsAndVisibilities } from "@Client/@Composables/application-standalone/pages/assets/assetsPageBindingsAndVisibilities";
import { get } from "lodash";
export default defineComponent({
  name: "AssetInformation",
  components: {
    PageSectionGroupLayout,
    SectionTitle,
    TextField,
    SelectField,
    CurrencyField
  },
  props: {
    applicantIndex: {
      type: Number,
      required: true
    },
    assetIndex: {
      type: Number,
      required: true
    },
    applicantId: {
      type: String,
      required: true
    },
    showErrors: {
      type: Number,
      default: 0
    }
  },
  setup(props, {
    emit
  }) {
    const {
      applicantIndex,
      assetIndex,
      applicantId,
      showErrors
    } = toRefs(props);
    const {
      t
    } = useI18n();
    const {
      applicantNameFromIndex,
      allApplicants
    } = useApplicantHelpers();
    const applicantName = applicantNameFromIndex(applicantIndex.value);
    const {
      bindings,
      visibilities
    } = assetsPageBindingsAndVisibilities(applicantIndex, assetIndex, applicantId);
    const {
      assetType,
      assetDescription,
      assetAmount,
      assetOwner
    } = bindings;
    const {
      assetTypeOptions
    } = APPLICATION_FIELD_OPTIONS();
    const {
      filteredApplicantsAsOptions
    } = useAssetHelpers();
    const assetDescriptionPlaceholder = computed(() => {
      var _pathByType$assetType;
      const pathByType = {
        [ASSET_TYPE.VEHICLE]: "vehiclePlaceholder",
        [ASSET_TYPE.GIFT]: "giftPlaceholder",
        [ASSET_TYPE.RRSP]: "bankPlaceholder",
        [ASSET_TYPE.OTHER]: "describeYourAsset"
      };
      const path = (_pathByType$assetType = pathByType[assetType.value]) != null ? _pathByType$assetType : "additionalDescription";
      return t(`application-standalone.pages.assets.${path}`);
    });
    const handleOwnerChange = newApplicantIndex => {
      if (typeof newApplicantIndex !== "number") return;
      assetOwner.value = newApplicantIndex;
      const applicant = allApplicants.value[newApplicantIndex];
      if (!applicant) return;
      const newAssetIndex = applicant.assets.length - 1;
      const newApplicantId = applicant.id;
      emit("navigate", {
        applicantIndex: newApplicantIndex,
        applicantId: newApplicantId,
        assetIndex: newAssetIndex
      });
    };
    const {
      assetTypeVisible,
      assetDescriptionVisible,
      assetAmountVisible
    } = visibilities;
    const {
      v$
    } = assetValidator(applicantIndex, assetIndex);
    const isValid = computed(() => {
      var _v$$value;
      return !(v$ != null && (_v$$value = v$.value) != null && _v$$value.$invalid);
    });
    watch(isValid, newVal => {
      emit("disable-save", !newVal);
    }, {
      immediate: true
    });
    watch(showErrors, newVal => {
      if (newVal) {
        var _v$$value2;
        v$ == null ? void 0 : (_v$$value2 = v$.value) == null ? void 0 : _v$$value2.$touch();
      }
    });
    const getValidator = path => get(v$.value, path, null);
    return {
      applicantName,
      assetType,
      assetDescription,
      assetAmount,
      assetOwner,
      assetTypeOptions,
      applicantsAsOptions: filteredApplicantsAsOptions,
      handleOwnerChange,
      assetTypeVisible,
      assetDescriptionVisible,
      assetAmountVisible,
      v$,
      assetDescriptionPlaceholder,
      getValidator
    };
  }
});